import { Component, inject, signal } from '@angular/core';
import {
  FaIconComponent,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';
import { faChevronDown, faShare } from '@fortawesome/free-solid-svg-icons';
import { FlowpilotChatService } from '@core/components/sidebar/components/flowpilot/window/content/services/flowpilot-chat.service';
import {
  InputComponent,
  MenuComponent,
  MenuTriggerComponent,
  TooltipTriggerDirective,
} from '@group24ag/flowkit';
import { FlowpilotChatActionComponent } from '@core/components/sidebar/components/flowpilot/window/content/components/chats/flowpilot-chat-action.component';
import { NgClass } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { KeyboardService } from '@core/services/keyboard.service';
import { KeyValue } from '@core/enums/key-parameters';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-flowpilot-chat-title',
  imports: [
    FaIconComponent,
    TooltipTriggerDirective,
    FlowpilotChatActionComponent,
    MenuComponent,
    MenuTriggerComponent,
    NgClass,
    InputComponent,
    ReactiveFormsModule,
  ],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
  template: `
    @if (selectedChat()) {
      <div class="flex w-full min-w-0 items-center">
        <div
          class="group flex h-min max-w-2/3 min-w-0 cursor-pointer items-center gap-x-0.5 pb-0.5">
          @if (!renameChat()) {
            <div
              class="group/text flex h-9 max-w-full min-w-0 items-center rounded-l-lg px-2.5"
              [ngClass]="getContainerClass(trigger.isOpen())"
              (click)="renameChatClick()">
              <span
                class="dark:text-dune-100/90 group-hover/text:dark:text-dune-0 group-hover/text:text-dune-1000 text-dune-700 truncate text-sm">
                {{ selectedChat()!.name }}
              </span>
            </div>

            <div
              #trigger
              flkMenuTrigger
              class="group/icon flex size-9 shrink-0 items-center justify-center rounded-r-lg"
              [ngClass]="getContainerClass(trigger.isOpen())"
              [triggerFor]="menu"
              [preferredPosition]="'bottomRight'">
              <fa-icon
                class="fa-xs mb-px"
                [ngClass]="getIconClass(trigger.isOpen())"
                [icon]="['fas', 'chevron-down']" />

              <ng-template #menu>
                <flk-menu>
                  <app-flowpilot-chat-action
                    [pinned]="!!selectedChat()?.pinned" />
                </flk-menu>
              </ng-template>
            </div>
          } @else {
            <flk-input
              class="-mt-2 w-[250px] max-w-full"
              [formControl]="nameControl!"
              [focusOnInit]="true"
              (blurred)="onInputBlur()"
              (keydown.enter)="onEnter()"
              (keydown.escape)="onEscape()" />
          }
        </div>
      </div>

      <div class="flex items-center">
        <fa-icon
          flkTooltipTrigger
          class="fa-sm dark:text-dune-200 dark:hover:bg-dune-800 hover:bg-dune-200/40 text-dune-500 size-[22px] cursor-pointer rounded"
          tooltip="Share chat"
          [icon]="['fas', 'share']" />
      </div>
    }
  `,
})
export class FlowpilotChatTitleComponent {
  /** INJECTIONS **/
  /****/
  private readonly chatService = inject(FlowpilotChatService);

  /****/
  private readonly keyBoardService = inject(KeyboardService);

  /** PROPERTIES **/
  /****/
  protected readonly selectedChat = this.chatService.selectedChatSignal;

  /****/
  protected readonly renameChat = signal(false);

  /****/
  protected nameControl: FormControl | null = null;

  constructor(library: FaIconLibrary) {
    library.addIcons(faChevronDown, faShare);

    this.keyBoardService
      .onKeyValueDown(KeyValue.F2)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.renameChat()) return;

        this.renameChatClick();
      });
  }

  protected getContainerClass(isOpen: boolean) {
    return {
      'group-hover:dark:bg-dune-900 group-hover:bg-dune-100': !isOpen,
      'dark:bg-dune-900 bg-dune-100': isOpen,
    };
  }

  protected getIconClass(isOpen: boolean) {
    return {
      'group-hover/icon:dark:text-dune-0 group-hover/icon:text-dune-1000 dark:text-dune-100/90 text-dune-600':
        !isOpen,
      'dark:text-dune-0 text-dune-1000': isOpen,
    };
  }

  protected renameChatClick(): void {
    if (!this.selectedChat()) return;

    this.nameControl = new FormControl(this.selectedChat()!.name, [
      Validators.required,
    ]);
    this.nameControl.markAsTouched();

    this.renameChat.set(true);
  }

  protected onInputBlur(): void {
    this.handleRename();
    this.renameChat.set(false);
    this.nameControl = null;
  }

  private handleRename() {
    const newName = this.nameControl?.value;
    if (!newName) return;

    this.chatService.updateChatName(this.selectedChat()!.id, newName);
  }

  protected onEnter() {
    if (!this.renameChat()) return;

    this.onInputBlur();
  }

  protected onEscape() {
    if (!this.renameChat()) return;

    this.renameChat.set(false);
    this.nameControl = null;
  }
}
